import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { Button } from "@/components/ui/button";
import { Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { supportLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

const MAX_FILES = 5;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPT_TYPES = ".pdf,.jpg,.jpeg,.png,.gif,.webp";

function isAllowedSupportFile(file: File): boolean {
  if (file.type === "application/pdf" || file.type.startsWith("image/")) {
    return true;
  }
  const lower = file.name.toLowerCase();
  return /\.(pdf|jpe?g|png|gif|webp)$/i.test(lower);
}

interface SupportCaseFileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  title?: string;
}

export function SupportCaseFileUpload({
  files,
  onFilesChange,
  title,
}: SupportCaseFileUploadProps) {
  const labels = supportLabels(getLanguage());

  const handleFilesSelected = (incoming: File[]) => {
    const invalidType = incoming.filter((file) => !isAllowedSupportFile(file));
    if (invalidType.length > 0) {
      toast.error(labels.invalidFileType, {
        description: labels.invalidFileTypeDescription,
      });
      return;
    }

    if (files.length + incoming.length > MAX_FILES) {
      toast.error(labels.tooManyFiles, {
        description: labels.tooManyFilesDescription,
      });
      return;
    }

    const oversized = incoming.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      toast.error(labels.filesTooLarge, {
        description: labels.filesTooLargeDescription.replace(
          "{files}",
          oversized.map((f) => f.name).join(", "),
        ),
      });
      return;
    }

    onFilesChange([...files, ...incoming]);
  };

  const handleRemove = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div>
      <h3 className="text-lg font-medium mb-2">
        {title ?? labels.uploadDocuments}
      </h3>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
        <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-600 mb-2">{labels.uploadDescription}</p>
        <p className="text-xs text-gray-500 mb-4">{labels.uploadRestrictions}</p>
        <FileUploadTrigger
          accept={ACCEPT_TYPES}
          multiple
          onFilesSelected={handleFilesSelected}
        >
          {(open) => (
            <Button
              type="button"
              variant="outline"
              className="cursor-pointer"
              onClick={open}
            >
              {labels.selectFiles}
            </Button>
          )}
        </FileUploadTrigger>
      </div>
      {files.length > 0 && (
        <div className="space-y-2 mt-4">
          <h4 className="text-sm font-medium">{labels.selectedFiles}</h4>
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between p-2 bg-primary/10 rounded"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                <span className="text-sm truncate">{file.name}</span>
                <span className="text-xs text-gray-500 shrink-0">
                  ({(file.size / 1024 / 1024).toFixed(1)} MB)
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(index)}
                className="shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const SUPPORT_CASE_FILE_LIMITS = {
  maxFiles: MAX_FILES,
  maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
  acceptTypes: ACCEPT_TYPES,
};
